(() => {
  const AUTO_ADVANCE_DELAY = 7200;
  const IMAGE_SWAP_DELAY = 260;
  const COPY_REVEAL_DELAY = 1000;
  const reducedMotion = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const humanFamily = (family) => ({ 'for-him': 'For him', 'for-her': 'For her', signature: 'Signature' }[family] || family);

  function initialize(root) {
    if (!root || root.dataset.ie2Bound) return;
    root.dataset.ie2Bound = 'true';

    const explorer = root.querySelector('#ie2-explorer');
    const choices = Array.from(root.querySelectorAll('[data-ie2-scent]'));
    const bottle = root.querySelector('[data-ie2-bottle]');
    const backdrop = root.querySelector('.ie2-explorer__backdrop');
    const name = root.querySelector('[data-ie2-name]');
    const inspired = root.querySelector('[data-ie2-inspired]');
    const family = root.querySelector('[data-ie2-family]');
    const top = root.querySelector('[data-ie2-top]');
    const heart = root.querySelector('[data-ie2-heart]');
    const base = root.querySelector('[data-ie2-base]');
    const autoplayToggle = root.querySelector('[data-ie2-toggle-autoplay]');
    const autoplayIcon = autoplayToggle?.querySelector('[aria-hidden="true"]');
    const transitionTimers = new Set();
    let activeIndex = 0;
    let transitionId = 0;
    let backdropId = 0;
    let autoplayTimer = null;
    let isPlaying = !reducedMotion();
    let isInView = false;
    let isPageVisible = !document.hidden;
    let isInteracting = false;

    if (!choices.length) return;

    function queue(callback, delay) {
      const timer = window.setTimeout(() => {
        transitionTimers.delete(timer);
        callback();
      }, delay);
      transitionTimers.add(timer);
      return timer;
    }

    function clearTransitionTimers() {
      transitionTimers.forEach((timer) => window.clearTimeout(timer));
      transitionTimers.clear();
    }

    function clearAutoplay() {
      if (autoplayTimer) window.clearTimeout(autoplayTimer);
      autoplayTimer = null;
    }

    function updateAutoplayControl() {
      if (!autoplayToggle) return;
      autoplayToggle.setAttribute('aria-pressed', String(isPlaying));
      autoplayToggle.setAttribute('aria-label', `${isPlaying ? 'Pause' : 'Play'} automatic fragrance carousel`);
      if (autoplayIcon) autoplayIcon.textContent = isPlaying ? 'Ⅱ' : '▶';
    }

    function updateActiveChoice(choice) {
      activeIndex = Math.max(0, choices.indexOf(choice));
      choices.forEach((item) => {
        const isActive = item === choice;
        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-pressed', String(isActive));
      });
    }

    function updateCopy(choice) {
      if (name) name.textContent = choice.dataset.name;
      if (inspired) inspired.textContent = choice.dataset.inspired;
      if (family) family.textContent = humanFamily(choice.dataset.family);
      if (top) top.textContent = choice.dataset.top;
      if (heart) heart.textContent = choice.dataset.heart;
      if (base) base.textContent = choice.dataset.base;
    }

    function updateSlideTheme(choice) {
      if (!explorer) return;
      explorer.style.setProperty('--ie2-slide-accent', choice.dataset.accent || '#d8ba78');
      explorer.style.setProperty('--ie2-slide-accent-light', choice.dataset.accentLight || '#f2dca8');
    }

    function updateBackdrop(choice, immediate = false) {
      const source = choice.dataset.backdrop;
      if (!backdrop || !source) return;
      if (backdrop.getAttribute('src') === source) return;
      backdropId += 1;
      const thisBackdrop = backdropId;

      const applySource = () => {
        if (thisBackdrop !== backdropId) return;
        backdrop.src = source;
        window.requestAnimationFrame(() => backdrop.classList.remove('is-changing'));
      };

      if (immediate || reducedMotion()) {
        applySource();
        return;
      }

      backdrop.classList.add('is-changing');
      const preloader = new Image();
      preloader.onload = applySource;
      preloader.onerror = applySource;
      preloader.src = source;
      window.setTimeout(applySource, 650);
    }

    function scheduleAutoplay() {
      clearAutoplay();
      if (!isPlaying || reducedMotion() || !isInView || !isPageVisible || isInteracting) return;

      autoplayTimer = window.setTimeout(() => {
        autoplayTimer = null;
        const nextChoice = choices[(activeIndex + 1) % choices.length];
        select(nextChoice, { revealDot: false });
      }, AUTO_ADVANCE_DELAY);
    }

    function transitionTo(choice, immediate) {
      clearTransitionTimers();
      transitionId += 1;
      const thisTransition = transitionId;
      updateSlideTheme(choice);
      updateBackdrop(choice, immediate);
      updateCopy(choice);

      if (!explorer || !bottle || immediate || reducedMotion()) {
        if (explorer) explorer.classList.remove('is-bottle-hidden', 'is-copy-hidden');
        if (bottle) {
          bottle.src = choice.dataset.image || bottle.src;
          bottle.alt = `${choice.dataset.name} travel perfume`;
        }
        scheduleAutoplay();
        return;
      }

      explorer.classList.add('is-bottle-hidden', 'is-copy-hidden');
      const imageSource = choice.dataset.image || bottle.src;
      let imageReady = false;
      let exitCompleted = false;
      let hasRevealed = false;

      const revealImage = () => {
        if (hasRevealed || thisTransition !== transitionId || !imageReady || !exitCompleted) return;
        hasRevealed = true;
        bottle.src = imageSource;
        bottle.alt = `${choice.dataset.name} travel perfume`;
        window.requestAnimationFrame(() => {
          if (thisTransition === transitionId) explorer.classList.remove('is-bottle-hidden');
        });

        queue(() => {
          if (thisTransition !== transitionId) return;
          explorer.classList.remove('is-copy-hidden');
          scheduleAutoplay();
        }, COPY_REVEAL_DELAY);
      };

      const preloader = new Image();
      const markImageReady = () => {
        if (thisTransition !== transitionId) return;
        imageReady = true;
        revealImage();
      };
      preloader.onload = markImageReady;
      preloader.onerror = markImageReady;
      preloader.src = imageSource;

      queue(() => {
        if (thisTransition !== transitionId) return;
        exitCompleted = true;
        revealImage();
      }, IMAGE_SWAP_DELAY);

      /* Do not leave the carousel blank if a slow network delays an image event. */
      queue(() => {
        if (thisTransition !== transitionId || imageReady) return;
        imageReady = true;
        revealImage();
      }, 800);
    }

    function select(choice, options = {}) {
      if (!choice) return;
      const { scrollExplorer = false, revealDot = true, immediate = false } = options;
      clearAutoplay();
      updateActiveChoice(choice);
      transitionTo(choice, immediate);

      if (revealDot) {
        choice.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
      }
      if (scrollExplorer && explorer) {
        explorer.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
      }
    }

    function pauseForInteraction() {
      isInteracting = true;
      clearAutoplay();
    }

    function resumeAfterInteraction() {
      isInteracting = false;
      scheduleAutoplay();
    }

    updateActiveChoice(choices[activeIndex]);
    transitionTo(choices[activeIndex], true);
    updateAutoplayControl();

    choices.forEach((choice) => choice.addEventListener('click', () => select(choice)));
    root.querySelector('[data-ie2-prev]')?.addEventListener('click', () => select(choices[(activeIndex - 1 + choices.length) % choices.length]));
    root.querySelector('[data-ie2-next]')?.addEventListener('click', () => select(choices[(activeIndex + 1) % choices.length]));

    root.querySelectorAll('[data-ie2-category]').forEach((category) => {
      category.addEventListener('click', () => {
        const choice = choices.find((item) => item.dataset.family === category.dataset.ie2Category);
        select(choice, { scrollExplorer: true });
      });
    });

    const categoryRail = root.querySelector('[data-ie2-category-rail]');
    root.querySelectorAll('[data-ie2-category-scroll]').forEach((control) => {
      control.addEventListener('click', () => {
        if (!categoryRail) return;
        const direction = Number(control.dataset.ie2CategoryScroll) || 1;
        categoryRail.scrollBy({ left: direction * categoryRail.clientWidth * .84, behavior: reducedMotion() ? 'auto' : 'smooth' });
      });
    });

    root.querySelectorAll('[data-ie2-card-select]').forEach((card) => {
      card.addEventListener('click', () => {
        select(choices.find((item) => item.dataset.name === card.dataset.ie2CardSelect), { scrollExplorer: true });
      });
    });

    autoplayToggle?.addEventListener('click', () => {
      isPlaying = !isPlaying;
      updateAutoplayControl();
      scheduleAutoplay();
    });

    const interactionRegion = explorer || root;
    interactionRegion.addEventListener('mouseenter', pauseForInteraction);
    interactionRegion.addEventListener('mouseleave', resumeAfterInteraction);
    interactionRegion.addEventListener('focusin', pauseForInteraction);
    interactionRegion.addEventListener('focusout', (event) => {
      if (!interactionRegion.contains(event.relatedTarget)) resumeAfterInteraction();
    });

    document.addEventListener('visibilitychange', () => {
      isPageVisible = !document.hidden;
      if (isPageVisible) scheduleAutoplay();
      else clearAutoplay();
    });

    if (explorer && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        isInView = entries.some((entry) => entry.isIntersecting);
        if (isInView) scheduleAutoplay();
        else clearAutoplay();
      }, { threshold: .35 });
      observer.observe(explorer);
    } else {
      isInView = true;
      scheduleAutoplay();
    }
  }

  function boot(scope = document) {
    scope.querySelectorAll?.('[data-ie2]').forEach(initialize);
  }

  document.addEventListener('DOMContentLoaded', () => boot());
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
